/**
 * Profile settings — interaction style, themes, notes, risk tolerance.
 * Shapes how Meeri talks to you (Butler config).
 */
import { useEffect, useState } from 'react';
import * as api from '../../lib/api';
import { useToast } from '../../lib/toast';

export function ProfileSettings() {
  const toast = useToast();
  const [style, setStyle] = useState<'socratic' | 'balanced' | 'agentic'>('balanced');
  const [risk, setRisk] = useState(3);
  const [horizon, setHorizon] = useState<'intraday' | 'swing' | 'long'>('long');
  const [themesText, setThemesText] = useState('');
  const [conviction, setConviction] = useState(6);
  const [note, setNote] = useState('');
  const [marketSignal, setMarketSignal] = useState<'off' | 'extremes' | 'always'>('extremes');
  const [busy, setBusy] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  useEffect(() => {
    api.me.getPreferences()
      .then((r) => {
        setStyle(r.interaction_style);
        setRisk(r.risk_tolerance);
        setHorizon(r.time_horizon);
        setThemesText((r.themes ?? []).join(', '));
        setConviction(r.min_conviction_to_propose);
        setNote(r.note ?? '');
        setMarketSignal(r.market_signal_mode ?? 'extremes');
        setLastUpdated(r.updated_at?.split('T')[0] ?? null);
      })
      .catch((e) => toast.show(e.message, 'bad'));
  }, []);

  async function save() {
    setBusy(true);
    try {
      const r = await api.me.setPreferences({
        interaction_style: style,
        risk_tolerance: risk,
        time_horizon: horizon,
        themes: themesText.split(',').map((s) => s.trim()).filter(Boolean),
        min_conviction_to_propose: conviction,
        note: note || null,
        market_signal_mode: marketSignal,
      });
      setLastUpdated(r.updated_at?.split('T')[0] ?? null);
      toast.show('Profile saved', 'ok');
    } catch (e: unknown) {
      toast.show((e as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Section
      title="Interaction style"
      subtitle="Three perspectives talk to you in chat: the Concierge (does the work), the Contrarian (blind spots), and Market Signal (what the market is doing). These knobs decide how loud each one is."
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
        <label className="block col-span-full">
          <span className="text-ink-mute">interaction style</span>
          <select className="input w-full mt-1" value={style} onChange={(e) => setStyle(e.target.value as typeof style)}>
            <option value="socratic">socratic — asks sharpening questions, always shows the counterview</option>
            <option value="balanced">balanced (default) — does the work, pauses at the decision point</option>
            <option value="agentic">agentic — proposes decisively, you veto via approval framework</option>
          </select>
        </label>
        <label className="block col-span-full">
          <span className="text-ink-mute">market signal</span>
          <select className="input w-full mt-1" value={marketSignal} onChange={(e) => setMarketSignal(e.target.value as typeof marketSignal)}>
            <option value="extremes">extremes-only (default)</option>
            <option value="always">always</option>
            <option value="off">off</option>
          </select>
        </label>
        <label className="block">
          <span className="text-ink-mute">risk tolerance (1-5)</span>
          <input type="number" className="input w-full mt-1 num" min={1} max={5} value={risk} onChange={(e) => setRisk(Number(e.target.value))} />
        </label>
        <label className="block">
          <span className="text-ink-mute">time horizon</span>
          <select className="input w-full mt-1" value={horizon} onChange={(e) => setHorizon(e.target.value as typeof horizon)}>
            <option value="intraday">intraday</option>
            <option value="swing">swing</option>
            <option value="long">long</option>
          </select>
        </label>
        <label className="block col-span-full">
          <span className="text-ink-mute">themes (comma-separated)</span>
          <input className="input w-full mt-1" value={themesText} onChange={(e) => setThemesText(e.target.value)} placeholder="e.g. AI agents, B2B SaaS, semis" />
        </label>
        <label className="block">
          <span className="text-ink-mute">min conviction to propose (1-10)</span>
          <input type="number" className="input w-full mt-1 num" min={1} max={10} value={conviction} onChange={(e) => setConviction(Number(e.target.value))} />
        </label>
        <label className="block col-span-full">
          <span className="text-ink-mute">note Meeri should remember</span>
          <textarea className="input w-full mt-1" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder='e.g. "always show the cost of capital"' />
        </label>
        <div className="col-span-full flex items-center gap-2">
          <button className="btn" onClick={save} disabled={busy}>save</button>
          {lastUpdated && <span className="text-ink-dim ml-auto">last updated {lastUpdated}</span>}
        </div>
      </div>
    </Section>
  );
}

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="card p-4">
      <div className="text-sm font-semibold mb-1">{title}</div>
      {subtitle && <div className="text-[11px] text-ink-mute mb-3">{subtitle}</div>}
      {children}
    </div>
  );
}
